import type {
  WorkflowTaskDetail,
  PropertyValueItem,
} from "../services/regulationsService";
import type {
  TaskTableDefinition,
  TaskTableFormState,
} from "../types/manualAssessmentWorkflow";
import type {
  ESignActionConfig,
  ESignPolicy,
  ResolvedESignAction,
} from "../types/eSignPolicy";
import { resolveTaskResultFromTaskTables } from "./manualAssessmentTaskResult";
import { taskDetailHasTaskTables } from "./taskDetailToFormConfig";

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

function parseActionConfig(raw: unknown): ESignActionConfig | null {
  const record = asRecord(raw);
  if (!record) return null;
  const meaning =
    typeof record.meaning === "string" ? record.meaning : undefined;
  const reason = typeof record.reason === "string" ? record.reason : undefined;
  if (meaning == null && reason == null) return null;
  return { meaning, reason };
}

/** Normalize API `eSignPolicy` / marker `esignIntegration` payloads. */
export function parseESignPolicy(raw: unknown): ESignPolicy | null {
  const record = asRecord(raw);
  if (!record) return null;

  const actionsRaw = asRecord(record.actions);
  const actions: Record<string, ESignActionConfig> = {};
  if (actionsRaw) {
    for (const [key, value] of Object.entries(actionsRaw)) {
      const parsed = parseActionConfig(value);
      if (parsed) actions[key] = parsed;
    }
  }

  return {
    schemaVersion:
      typeof record.schemaVersion === "number"
        ? record.schemaVersion
        : undefined,
    enabled: Boolean(record.enabled),
    triggerField:
      typeof record.triggerField === "string" && record.triggerField.trim()
        ? record.triggerField
        : "taskResult",
    actions,
  };
}

function findEsignIntegrationMarker(
  list: PropertyValueItem[] | undefined,
): ESignPolicy | null {
  if (!Array.isArray(list)) return null;
  for (const item of list) {
    if (item?.field !== "esignIntegrationMarker") continue;
    const fromNested = parseESignPolicy(
      (item as PropertyValueItem & { esignIntegration?: unknown })
        .esignIntegration,
    );
    if (fromNested) return fromNested;
  }
  return null;
}

/**
 * Prefer top-level `data.eSignPolicy`; fall back to hidden marker field.
 */
export function getESignPolicyFromTaskDetail(
  taskDetail: WorkflowTaskDetail | null | undefined,
): ESignPolicy | null {
  if (!taskDetail) return null;
  const topLevel = parseESignPolicy(
    (taskDetail as WorkflowTaskDetail & { eSignPolicy?: unknown }).eSignPolicy,
  );
  if (topLevel) return topLevel;
  return findEsignIntegrationMarker(taskDetail.task?.propertyValueItemList);
}

function readTriggerFieldFromFormData(
  formData: Record<string, unknown>,
  triggerField: string,
): unknown {
  if (
    formData[triggerField] !== undefined &&
    formData[triggerField] !== null &&
    formData[triggerField] !== ""
  ) {
    return formData[triggerField];
  }
  return undefined;
}

/**
 * Resolve the submit action key(s) used to look up `eSignPolicy.actions`.
 * Mirrors taskResult resolution used by `buildSubmitTaskPayload` when
 * `triggerField` is `taskResult`.
 */
export function resolveESignTriggerValue(input: {
  taskDetail: WorkflowTaskDetail | null | undefined;
  formData: Record<string, unknown>;
  triggerField: string;
  action?: string;
  taskTables?: TaskTableDefinition[];
  taskTableState?: TaskTableFormState;
}): unknown {
  const {
    taskDetail,
    formData,
    triggerField,
    action,
    taskTables,
    taskTableState = {},
  } = input;

  const fromForm = readTriggerFieldFromFormData(formData, triggerField);
  if (fromForm !== undefined) return fromForm;

  if (triggerField === "taskResult") {
    if (action === "approve") return "Approve";
    if (action === "reject") return "Reject";

    if (taskDetailHasTaskTables(taskDetail)) {
      return resolveTaskResultFromTaskTables(taskTables, taskTableState) ?? "";
    }
  }

  return undefined;
}

function candidateActionKeys(
  triggerValue: unknown,
  buttonAction?: string,
): string[] {
  const keys: string[] = [];
  const push = (key: string) => {
    if (key && !keys.includes(key)) keys.push(key);
  };

  if (Array.isArray(triggerValue)) {
    const parts = triggerValue.map((v) => String(v));
    push(parts.join(","));
    for (const part of parts) push(part);

    const lower = parts.map((p) => p.toLowerCase());
    const hasRejectOrReopen = lower.some(
      (p) => p.includes("reject") || p.includes("reopen"),
    );
    const allApproveLike =
      parts.length > 0 &&
      !hasRejectOrReopen &&
      lower.every(
        (p) =>
          p.includes("approve") ||
          p === "reviewed" ||
          p.includes("approve_all"),
      );
    if (allApproveLike) {
      push("Approve");
      push("approve");
    }
  } else if (triggerValue != null && triggerValue !== "") {
    push(String(triggerValue));
  }

  if (buttonAction === "approve") {
    push("Approve");
    push("approved");
    push("Reviewed");
    push("reviewed");
  } else if (buttonAction === "reject") {
    push("Reject");
    push("rejected");
  }

  return keys;
}

function lookupAction(
  actions: Record<string, ESignActionConfig>,
  keys: string[],
): ResolvedESignAction | null {
  for (const key of keys) {
    const match = actions[key];
    if (!match) continue;
    return {
      actionKey: key,
      meaning: match.meaning ?? key,
      reason: match.reason ?? "",
    };
  }
  return null;
}

/**
 * Returns e-sign props when policy is enabled and the current submit action
 * is listed under `actions`; otherwise `null` (submit without popup).
 */
export function resolveESignActionForSubmit(input: {
  taskDetail: WorkflowTaskDetail | null | undefined;
  formData: Record<string, unknown>;
  action?: string;
  taskTables?: TaskTableDefinition[];
  taskTableState?: TaskTableFormState;
}): ResolvedESignAction | null {
  const policy = getESignPolicyFromTaskDetail(input.taskDetail);
  if (!policy?.enabled) return null;
  if (!policy.actions || Object.keys(policy.actions).length === 0) return null;

  const triggerField = policy.triggerField || "taskResult";
  const triggerValue = resolveESignTriggerValue({
    taskDetail: input.taskDetail,
    formData: input.formData,
    triggerField,
    action: input.action,
    taskTables: input.taskTables,
    taskTableState: input.taskTableState,
  });

  return lookupAction(
    policy.actions,
    candidateActionKeys(triggerValue, input.action),
  );
}
